import { useTranslation } from '@rhino-project/core/i18n';
import { useEffect } from 'react';
import { formatDistance } from '@rhino-project/core/i18n/format';
import { Icon } from '@iconify/react';
import {
  useCable,
  useModelIndex,
  useModelInvalidateIndex,
  useModelUpdate
} from '@rhino-project/core/hooks';
import {
  NOTIFICATION_MODEL,
  useNotificationsOpenAll
} from '@rhino-project/core/queries';
import { RhinoRecordIdentifier } from '@rhino-project/core';
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownPopover,
  DropdownTrigger,
  Separator
} from '@heroui/react';

interface NotificationRow {
  id: RhinoRecordIdentifier;
  message: string;
  url: string | null;
  created_at?: string;
}

export const NotificationMenu = () => {
  const { t } = useTranslation();
  const { results, total } = useModelIndex(NOTIFICATION_MODEL, {
    filter: { opened_at: { is_null: true } },
    order: ['-created_at'],
    limit: 10
  });
  const notifications = (results ?? []) as NotificationRow[];
  const { invalidate } = useModelInvalidateIndex(NOTIFICATION_MODEL);
  const { mutate: openOne } = useModelUpdate(NOTIFICATION_MODEL);
  const { mutate: openAll } = useNotificationsOpenAll();

  const consumer = useCable();
  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      'Rhino::NotificationsChannel',
      { received: () => invalidate() }
    );
    return () => {
      subscription.unsubscribe();
    };
  }, [consumer, invalidate]);

  const unreadCount = total ?? 0;
  const hasNotifications = unreadCount > 0;

  const handleItemClick = (notificationId: RhinoRecordIdentifier) =>
    openOne({
      id: notificationId,
      opened_at: new Date().toISOString()
    } as unknown as Parameters<typeof openOne>[0]);

  const items = hasNotifications
    ? notifications.map((n) => (
        <DropdownItem
          id={String(n.id)}
          key={n.id}
          textValue={n.message}
          href={n.url ?? undefined}
          onPress={() => handleItemClick(n.id)}
        >
          <span className="flex w-full flex-col gap-0.5 py-1">
            <span className="whitespace-normal text-sm leading-snug line-clamp-2">
              {n.message}
            </span>
            {n.created_at && (
              <span className="text-xs text-muted">
                {formatDistance(new Date(n.created_at))}
              </span>
            )}
          </span>
        </DropdownItem>
      ))
    : [
        <DropdownItem
          id="empty"
          key="empty"
          textValue={t('rhino.frontend.messages.no_unread_notifications')}
          isDisabled
        >
          <span className="flex w-full flex-col items-center gap-1 py-6 text-center">
            <Icon icon="bi:bell-slash" className="text-xl text-muted" />
            <span className="text-sm font-medium">
              {t('rhino.frontend.messages.all_caught_up')}
            </span>
            <span className="text-xs text-muted">
              {t('rhino.frontend.messages.no_unread_notifications_detail')}
            </span>
          </span>
        </DropdownItem>
      ];

  return (
    <Dropdown>
      <DropdownTrigger className="flex w-full cursor-pointer items-center gap-4 rounded-lg px-2 py-2 text-foreground/80 hover:bg-surface-secondary hover:text-foreground">
        <Icon icon="bi:bell" className="text-lg" />
        <span className="text-sm">
          {t('rhino.frontend.messages.notifications')}
        </span>
        {hasNotifications && (
          <Chip color="accent" size="sm" className="ml-auto">
            {unreadCount}
          </Chip>
        )}
      </DropdownTrigger>
      <DropdownPopover placement="top start" className="w-80">
        <div className="flex min-h-9 items-center justify-between gap-2 py-1 pl-2 pr-1">
          <span className="text-xs text-muted">
            {t('rhino.frontend.messages.notifications')}
          </span>
          {hasNotifications && (
            <Button variant="ghost" size="sm" onPress={() => openAll()}>
              <Icon icon="bi:check2-all" />
              {t('rhino.frontend.messages.mark_all_read')}
            </Button>
          )}
        </div>
        <Separator />
        <DropdownMenu
          aria-label={t('rhino.frontend.messages.notifications')}
          className="max-h-80 overflow-y-auto"
        >
          {items}
        </DropdownMenu>
        {unreadCount > notifications.length && (
          <div className="pb-1.5 pt-1 text-center text-xs text-muted">
            {t('rhino.frontend.messages.notification_count', {
              shown: notifications.length,
              count: unreadCount
            })}
          </div>
        )}
      </DropdownPopover>
    </Dropdown>
  );
};
